import React, { type FC, useCallback, useMemo, useState } from "react";
import { SLIDER_INCREASING } from "../../../../../../../../shared-logic/features/filters/utils/constants/filterConfigConstants";
import ButtonSlider from "./ButtonSlider";
import styles from "./PriceFilter.module.scss";
import filterButtonStyles from "./FilterButton.module.scss";
import type { FilterDataType } from "../../../../../utils/filterUtils";
import Text from "../../../../../../shared-components/css-modules/toyota/atoms/Text";
import { formatPrice } from "../../../../../../../../common-deprecated/Globalize";
import { useCommonSelector } from "../../../../../../../../common-deprecated/redux/commonStore";
import { useGradeSelectorLabel } from "../../../../../utils/labels";
import type { PriceRangeType } from "../../../../../utils/types/Model";
import { useGradeSelectorSelector } from "../../../../../redux/hooks";
import * as commonSettingUtils from "../../../../../../../../common-deprecated/settings/utils/commonSettingUtils";
import ButtonSliderRTL from "../../../../../../../../common-deprecated/features/filters/components/toyota/filters/ButtonSlider/ButtonSliderRTL";

type PriceFilterProps = {
    value: FilterDataType;
    onChange?: (payload: PriceRangeType) => void;
};

const PriceFilter: FC<PriceFilterProps> = ({ value, onChange }) => {
    const initialMinPrice = value.price?.minPrice || 0;
    const initialMaxPrice = value.price?.maxPrice || 0;
    const commonSettings = useCommonSelector((state) => state.commonSettings);
    const isRTL = commonSettingUtils.isRTL(commonSettings);

    const cultureName = useCommonSelector((state) => state.commonSettings.culture.name);
    const gradeSelectorPriceFilterLowest = useGradeSelectorLabel("gradeSelectorPriceFilterLowest");
    const gradeSelectorPriceFilterHighest = useGradeSelectorLabel("gradeSelectorPriceFilterHighest");
    const gradeSelectorFilterPriceButton = useGradeSelectorLabel("gradeSelectorFilterPriceButton");
    const currentSetPriceRange = useGradeSelectorSelector((state) => state.gradeSelectorFilters.filters.price);

    const [currentMinValue, setCurrentMinValue] = useState(
        currentSetPriceRange ? currentSetPriceRange.minPrice : initialMinPrice,
    );
    const [currentMaxValue, setCurrentMaxValue] = useState(
        currentSetPriceRange ? currentSetPriceRange.maxPrice : initialMaxPrice,
    );
    const [sliderLabel, setSliderLabel] = useState("");

    const handleSetValue = useCallback(
        (handledValue: number, type: "min" | "max") => {
            if (type === "min") {
                setCurrentMinValue(handledValue);
            } else {
                setCurrentMaxValue(handledValue);
            }

            onChange?.({
                minPrice: type === "min" ? handledValue : currentMinValue,
                maxPrice: type === "max" ? handledValue : currentMaxValue,
            });
        },
        [onChange],
    );

    const getValueLabelFn = useCallback(
        (max: number, min: number): SanitizedString => {
            return `${formatPrice(min, cultureName)} - ${formatPrice(max, cultureName)}` as SanitizedString;
        },
        [cultureName],
    );

    const formatPriceLabel = useCallback(
        (priceValue: number, label: string) => {
            const formattedPrice = formatPrice(priceValue, cultureName);
            return label.includes("{min}")
                ? label.replace("{min}", `${formattedPrice}`)
                : label.replace("{max}", `${formattedPrice}`);
        },
        [cultureName],
    );

    const PriceRangeLabels = useMemo(
        () => (
            <div className={styles.priceRange}>
                <Text>{formatPriceLabel(initialMinPrice, gradeSelectorPriceFilterLowest)}</Text>
                <Text>{formatPriceLabel(initialMaxPrice, gradeSelectorPriceFilterHighest)}</Text>
            </div>
        ),
        [
            initialMinPrice,
            initialMaxPrice,
            gradeSelectorPriceFilterLowest,
            gradeSelectorPriceFilterHighest,
            formatPriceLabel,
        ],
    );

    const buttonSliderProps = {
        minValue: initialMinPrice,
        maxValue: initialMaxPrice,
        step: 100,
        currentMinValue,
        currentMaxValue,
        setValue: handleSetValue,
        enableMinValue: true,
        order: SLIDER_INCREASING,
        getValueLabel: getValueLabelFn,
        setValueLabel: setSliderLabel,
        filterName: gradeSelectorFilterPriceButton,
    };

    return (
        <div className={filterButtonStyles.priceFilter}>
            <div className={styles.results}>
                <Text tag="p" color="grey-7">
                    {gradeSelectorFilterPriceButton}
                </Text>
                <Text>{sliderLabel}</Text>
            </div>
            {isRTL ? <ButtonSliderRTL {...buttonSliderProps} /> : <ButtonSlider {...buttonSliderProps} />}

            {PriceRangeLabels}
        </div>
    );
};

export default PriceFilter;
